
import React,{useEffect,useRef,useState}from"react";
import{Box,Button,FormControl,Select,MenuItem,Dialog,CircularProgress,Typography,LinearProgress,Grid}from"@mui/material";
import FileDownloadOutlinedIcon from"@mui/icons-material/FileDownloadOutlined";
import TableChartOutlinedIcon from"@mui/icons-material/TableChartOutlined";
import CodeRoundedIcon from"@mui/icons-material/CodeRounded";
import PictureAsPdfOutlinedIcon from"@mui/icons-material/PictureAsPdfOutlined";
import KeyboardArrowDownRoundedIcon from"@mui/icons-material/KeyboardArrowDownRounded";
import{useAuth}from"../../../context/AuthContext";
import{useAlert}from"../../../context/AlertContext";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";

const DOWNLOAD_OPTIONS=[
  {
    value:"excel",
    label:"Reporte Excel",
    description:"Reporte de ventas",
    endpoint:"/api/facturacion/reportes/ventas/excel/",
    extension:"xlsx",
    filename:"Reporte_Ventas",
    Icon:TableChartOutlinedIcon
  },
  {
    value:"xml",
    label:"Facturas XML",
    description:"Comprobantes autorizados",
    endpoint:"/api/facturacion/reportes/ventas/xml/",
    extension:"zip",
    filename:"Facturas_XML",
    Icon:CodeRoundedIcon
  },
  {
    value:"pdf",
    label:"Facturas PDF",
    description:"Facturas en formato PDF",
    extension:"zip",
    filename:"Facturas_PDF",
    Icon:PictureAsPdfOutlinedIcon
  }
];

const PDF_ENDPOINTS={
  preparar:"/api/facturacion/reportes/ventas/pdf/preparar/",
  lote:"/api/facturacion/reportes/ventas/pdf/lote/",
  descargar:"/api/facturacion/reportes/ventas/pdf/descargar/"
};

const INITIAL_PROGRESS={
  total:0,
  procesadas:0,
  pendientes:0,
  errores:0,
  porcentaje:0,
  etapa:"preparando"
};

function getFilename(response,fallback){
  const disposition=response.headers.get("Content-Disposition")||"";
  const encoded=disposition.match(/filename\*=UTF-8''([^;]+)/i);

  if(encoded?.[1]){
    try{
      return decodeURIComponent(encoded[1].replace(/["']/g,""));
    }catch{
      return fallback;
    }
  }

  const standard=disposition.match(/filename="?([^";]+)"?/i);
  return standard?.[1]?.trim()||fallback;
}

async function getErrorMessage(response){
  const contentType=response.headers.get("Content-Type")||"";

  if(contentType.includes("application/json")){
    const data=await response.json().catch(()=>({}));
    return data.detail||data.error||data.message||"No se pudo completar la descarga.";
  }

  const message=await response.text().catch(()=>"");
  return message||"No se pudo completar la descarga.";
}

function downloadBlob(blob,filename){
  if(!blob.size){
    throw new Error("El servidor devolvió un archivo vacío.");
  }

  const url=window.URL.createObjectURL(blob);
  const link=document.createElement("a");

  link.href=url;
  link.download=filename;
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.setTimeout(()=>{
    window.URL.revokeObjectURL(url);
  },1000);
}

function ProgressStat({label,value,color,background}){
  return(
    <Box
      sx={{
        p:1.5,
        height:"100%",
        borderRadius:"11px",
        bgcolor:background,
        textAlign:"center"
      }}
    >
      <Typography
        sx={{
          fontSize:{xs:23,sm:26},
          fontWeight:700,
          color,
          fontVariantNumeric:"tabular-nums",
          lineHeight:1.2
        }}
      >
        {Number(value||0).toLocaleString("es-EC")}
      </Typography>
      <Typography
        sx={{
          fontSize:12,
          fontWeight:500,
          color,
          mt:.5
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function Downloads({
  desde="",
  hasta="",
  canReports=false,
  disabled=false,
  onBusyChange
}){
  const{accessToken}=useAuth();
  const{showError,showSuccess}=useAlert();
  const{dashboardTheme}=useDashboardTheme();

  const[selected,setSelected]=useState("excel");
  const[downloading,setDownloading]=useState(false);
  const[menuOpen,setMenuOpen]=useState(false);
  const[progress,setProgress]=useState(INITIAL_PROGRESS);
  const[downloadType,setDownloadType]=useState("excel");

  const requestRef=useRef(null);
  const loadingRef=useRef(false);

  const option=DOWNLOAD_OPTIONS.find(item=>item.value===selected)||DOWNLOAD_OPTIONS[0];
  const blocked=disabled||downloading;

  const textColor=dashboardTheme.text_color;
  const mutedColor=dashboardTheme.muted_text_color;
  const buttonColor=dashboardTheme.button_color;
  const borderColor=dashboardTheme.border_color;
  const cardBackground=dashboardTheme.card_background;
  const dark=dashboardTheme.theme_mode==="dark";

  useEffect(()=>{
    return()=>{
      requestRef.current?.abort();
    };
  },[]);

  const validateDates=()=>{
    if(!desde||!hasta){
      showError("Debe seleccionar la fecha inicial y la fecha final.");
      return false;
    }

    if(desde>hasta){
      showError("La fecha inicial no puede ser mayor que la fecha final.");
      return false;
    }

    return true;
  };

  const apiRequest=async(url,{method="GET",body=null,signal}={})=>{
    const response=await fetch(url,{
      method,
      headers:{
        Authorization:`Bearer ${accessToken}`,
        ...(body!==null?{"Content-Type":"application/json"}:{})
      },
      ...(body!==null?{body:JSON.stringify(body)}:{}),
      signal,
      cache:"no-store"
    });

    if(!response.ok){
      throw new Error(await getErrorMessage(response));
    }

    return response;
  };

  const downloadFile=async(url,filename,signal)=>{
    const response=await apiRequest(url,{signal});
    const blob=await response.blob();
    downloadBlob(blob,getFilename(response,filename));
  };

  const downloadSimple=async(item,inicio,fin,signal)=>{
    const params=new URLSearchParams({
      fecha_inicio:inicio,
      fecha_fin:fin
    });

    await downloadFile(
      `${item.endpoint}?${params.toString()}`,
      `${item.filename}_${inicio}_${fin}.${item.extension}`,
      signal
    );
  };

  const downloadPdf=async(inicio,fin,signal)=>{
    setProgress(INITIAL_PROGRESS);

    const params=new URLSearchParams({
      fecha_inicio:inicio,
      fecha_fin:fin
    });

    const prepareResponse=await apiRequest(
      `${PDF_ENDPOINTS.preparar}?${params.toString()}`,
      {
        method:"POST",
        body:{},
        signal
      }
    );

    const preparation=await prepareResponse.json();

    if(!preparation.ok||!preparation.token){
      throw new Error(preparation.detail||"No se pudo preparar la descarga PDF.");
    }

    const total=Number(preparation.total||0);

    if(total<=0){
      throw new Error("No existen facturas autorizadas para las fechas seleccionadas.");
    }

    let offset=0;
    let errores=0;
    let primerError="";

    setProgress({
      total,
      procesadas:0,
      pendientes:total,
      errores:0,
      porcentaje:0,
      etapa:"procesando"
    });

    while(offset<total){
      if(signal.aborted){
        return;
      }

      const response=await apiRequest(PDF_ENDPOINTS.lote,{
        method:"POST",
        body:{
          token:preparation.token,
          offset
        },
        signal
      });

      const batch=await response.json();

      if(!batch.ok){
        throw new Error(batch.detail||"No se pudieron preparar las facturas.");
      }

      const next=Number(batch.procesadas);
      const batchErrors=Array.isArray(batch.errores_lote)?batch.errores_lote:[];

      if(!Number.isInteger(next)||next<=offset||next>total){
        throw new Error("El servidor devolvió un avance de procesamiento inválido.");
      }

      errores+=batchErrors.length;

      if(!primerError&&batchErrors.length){
        primerError=`${batchErrors[0].numero||"Factura"}: ${batchErrors[0].mensaje||"Error al preparar el documento"}`;
      }

      offset=next;

      setProgress({
        total,
        procesadas:offset,
        pendientes:Math.max(total-offset,0),
        errores,
        porcentaje:Math.min(100,Math.round(offset*100/total)),
        etapa:"procesando"
      });

      if(batch.completado&&offset<total){
        throw new Error("El procesamiento terminó antes de completar todas las facturas.");
      }
    }

    if(errores>0){
      setProgress(prev=>({
        ...prev,
        etapa:"error"
      }));

      throw new Error(
        `Se procesaron ${total} facturas, pero ${errores} presentaron errores. ${primerError} Intente nuevamente la descarga.`
      );
    }

    setProgress(prev=>({
      ...prev,
      etapa:"comprimiendo",
      porcentaje:100,
      pendientes:0
    }));

    const downloadParams=new URLSearchParams({
      token:preparation.token
    });

    await downloadFile(
      `${PDF_ENDPOINTS.descargar}?${downloadParams.toString()}`,
      `Facturas_PDF_${inicio}_${fin}.zip`,
      signal
    );

    setProgress(prev=>({
      ...prev,
      etapa:"completado"
    }));
  };

  const handleDownload=async()=>{
    if(loadingRef.current||blocked||!canReports){
      return;
    }

    if(!validateDates()){
      return;
    }

    if(!accessToken){
      showError("Su sesión no está disponible.");
      return;
    }

    const currentOption=option;
    const inicio=desde;
    const fin=hasta;
    const controller=new AbortController();

    requestRef.current=controller;
    loadingRef.current=true;

    setMenuOpen(false);
    setDownloadType(currentOption.value);
    setProgress(INITIAL_PROGRESS);
    setDownloading(true);
    onBusyChange?.(true);

    try{
      if(currentOption.value==="pdf"){
        await downloadPdf(inicio,fin,controller.signal);
      }else{
        await downloadSimple(currentOption,inicio,fin,controller.signal);
      }

      if(!controller.signal.aborted){
        showSuccess(
          currentOption.value==="excel"
            ?"Reporte Excel descargado correctamente."
            :currentOption.value==="xml"
              ?"Descarga de facturas XML iniciada correctamente."
              :"Facturas PDF preparadas y descarga iniciada correctamente."
        );
      }
    }catch(error){
      if(error.name!=="AbortError"){
        console.error("ERROR EN DESCARGA:",error);
        showError(error.message||"No se pudo completar la descarga.");
      }
    }finally{
      requestRef.current=null;
      loadingRef.current=false;
      setDownloading(false);
      onBusyChange?.(false);
    }
  };

  const getProgressMessage=()=>{
    if(downloadType==="excel"){
      return"Se está preparando el reporte de ventas en Excel.";
    }

    if(downloadType==="xml"){
      return"Se están preparando las facturas XML para su descarga.";
    }

    if(progress.etapa==="preparando"){
      return"Consultando las facturas del período seleccionado.";
    }

    if(progress.etapa==="comprimiendo"){
      return"Las facturas están listas. Preparando el archivo para descargar.";
    }

    if(progress.etapa==="completado"){
      return"Todos los documentos están listos.";
    }

    return"Estamos preparando sus facturas. Puede consultar el avance a continuación.";
  };

  const percentage=Math.max(0,Math.min(100,Number(progress.porcentaje||0)));

  return(
    <>
      <Box
        sx={{
          display:"flex",
          alignItems:"center",
          flexWrap:"wrap",
          gap:1,
          minWidth:0
        }}
      >
        <FormControl
          size="small"
          disabled={!canReports||blocked}
          sx={{
            minWidth:{xs:180,sm:210},
            maxWidth:"100%"
          }}
        >
          <Select
            value={selected}
            onChange={event=>setSelected(event.target.value)}
            open={menuOpen}
            onOpen={()=>setMenuOpen(true)}
            onClose={()=>setMenuOpen(false)}
            size="small"
            IconComponent={KeyboardArrowDownRoundedIcon}
            renderValue={value=>{
              const item=DOWNLOAD_OPTIONS.find(x=>x.value===value)||DOWNLOAD_OPTIONS[0];
              const ItemIcon=item.Icon;

              return(
                <Box sx={{display:"flex",alignItems:"center",gap:1,minWidth:0}}>
                  <ItemIcon sx={{fontSize:19,color:buttonColor,flexShrink:0}}/>
                  <Typography
                    sx={{
                      fontSize:13,
                      fontWeight:600,
                      color:textColor,
                      whiteSpace:"nowrap"
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              );
            }}
            MenuProps={{
              disableScrollLock:true,
              keepMounted:true,
              anchorOrigin:{
                vertical:"bottom",
                horizontal:"left"
              },
              transformOrigin:{
                vertical:"top",
                horizontal:"left"
              },
              PaperProps:{
                sx:{
                  mt:.7,
                  minWidth:230,
                  maxWidth:"calc(100vw - 24px)",
                  borderRadius:"12px",
                  bgcolor:cardBackground,
                  color:textColor,
                  border:`1px solid ${borderColor}`,
                  boxShadow:"0 8px 28px rgba(0,0,0,0.14)",
                  "& .MuiList-root":{py:.5}
                }
              }
            }}
            sx={{
              height:38,
              borderRadius:"10px",
              bgcolor:cardBackground,
              color:textColor,
              "& .MuiOutlinedInput-notchedOutline":{
                borderColor
              },
              "&:hover .MuiOutlinedInput-notchedOutline":{
                borderColor:buttonColor
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline":{
                borderColor:buttonColor,
                borderWidth:1
              },
              "& .MuiSelect-select":{
                display:"flex",
                alignItems:"center",
                py:.7,
                pl:1.3,
                pr:4
              },
              "& .MuiSelect-icon":{
                color:mutedColor,
                right:8
              }
            }}
          >
            {DOWNLOAD_OPTIONS.map(item=>{
              const ItemIcon=item.Icon;

              return(
                <MenuItem
                  key={item.value}
                  value={item.value}
                  sx={{
                    minHeight:54,
                    px:1.5,
                    py:.8,
                    gap:1.2,
                    borderRadius:"7px",
                    mx:.5,
                    my:.25,
                    "&.Mui-selected":{
                      bgcolor:`${buttonColor}12`
                    },
                    "&.Mui-selected:hover":{
                      bgcolor:`${buttonColor}20`
                    }
                  }}
                >
                  <ItemIcon sx={{fontSize:21,color:buttonColor,flexShrink:0}}/>
                  <Box sx={{minWidth:0}}>
                    <Typography
                      sx={{
                        fontSize:13,
                        fontWeight:600,
                        color:textColor,
                        lineHeight:1.4
                      }}
                    >
                      {item.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize:11.5,
                        color:mutedColor,
                        lineHeight:1.35
                      }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          size="small"
          disabled={!canReports||blocked}
          onClick={handleDownload}
          startIcon={<FileDownloadOutlinedIcon sx={{fontSize:19}}/>}
          sx={{
            height:38,
            minWidth:116,
            width:"auto",
            px:1.8,
            borderRadius:"10px",
            bgcolor:buttonColor,
            color:"#FFFFFF",
            fontSize:12.5,
            fontWeight:600,
            textTransform:"none",
            whiteSpace:"nowrap",
            boxShadow:"none",
            "&:hover":{
              bgcolor:buttonColor,
              filter:"brightness(0.92)",
              boxShadow:"none"
            },
            "&.Mui-disabled":{
              opacity:.5
            }
          }}
        >
          Descargar
        </Button>
      </Box>

      <Dialog
        open={downloading}
        disableEscapeKeyDown
        disableScrollLock
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx:{
            width:downloadType==="pdf"
              ?"min(440px, calc(100vw - 32px))"
              :"min(380px, calc(100vw - 32px))",
            m:2,
            p:{xs:2.5,sm:3},
            borderRadius:"18px",
            bgcolor:cardBackground,
            border:`1px solid ${borderColor}`,
            boxShadow:"none",
            textAlign:"center"
          }
        }}
        BackdropProps={{
          sx:{
            bgcolor:dark
              ?"rgba(0,0,0,0.72)"
              :"rgba(15,23,42,0.35)"
          }
        }}
      >
        <Box
          sx={{
            width:58,
            height:58,
            borderRadius:"50%",
            mx:"auto",
            mb:2,
            display:"flex",
            alignItems:"center",
            justifyContent:"center",
            bgcolor:`${buttonColor}15`
          }}
        >
          <CircularProgress size={30} thickness={4} sx={{color:buttonColor}}/>
        </Box>

        <Typography
          sx={{
            fontSize:19,
            fontWeight:700,
            color:textColor,
            mb:.8
          }}
        >
          {downloadType==="excel"
            ?"Generando reporte"
            :downloadType==="xml"
              ?"Preparando archivos XML"
              :progress.etapa==="preparando"
                ?"Consultando facturas"
                :progress.etapa==="comprimiendo"
                  ?"Preparando descarga"
                  :"Generando facturas PDF"}
        </Typography>

        <Typography
          sx={{
            fontSize:13.5,
            color:mutedColor,
            lineHeight:1.5,
            mb:downloadType==="pdf"?2.5:0
          }}
        >
          {getProgressMessage()}
        </Typography>

        {downloadType==="pdf"&&(
          <Box sx={{width:"100%",textAlign:"left"}}>
            <Box
              sx={{
                display:"flex",
                alignItems:"baseline",
                justifyContent:"space-between",
                gap:2,
                mb:1
              }}
            >
              <Typography
                sx={{
                  fontSize:{xs:20,sm:23},
                  fontWeight:700,
                  color:textColor,
                  fontVariantNumeric:"tabular-nums"
                }}
              >
                {progress.procesadas.toLocaleString("es-EC")}
                {" / "}
                {progress.total.toLocaleString("es-EC")}
              </Typography>

              <Typography
                sx={{
                  fontSize:15,
                  fontWeight:700,
                  color:buttonColor,
                  fontVariantNumeric:"tabular-nums"
                }}
              >
                {percentage}%
              </Typography>
            </Box>

            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height:10,
                borderRadius:"8px",
                bgcolor:dark?"rgba(255,255,255,0.12)":"#E5E7EB",
                "& .MuiLinearProgress-bar":{
                  borderRadius:"8px",
                  bgcolor:buttonColor,
                  transition:"transform 240ms ease"
                }
              }}
            />

            <Typography
              sx={{
                mt:1,
                fontSize:12,
                color:mutedColor,
                textAlign:"center"
              }}
            >
              {progress.etapa==="comprimiendo"
                ?"Preparando archivo de descarga"
                :progress.etapa==="preparando"
                  ?"Consultando documentos"
                  :"Preparando facturas"}
            </Typography>

            <Grid container spacing={1.2} sx={{mt:1.5}}>
              <Grid item xs={6}>
                <ProgressStat
                  label="Pendientes"
                  value={progress.pendientes}
                  color={dark?"#FDE68A":"#92400E"}
                  background={dark?"rgba(245,158,11,0.12)":"#FFFBEB"}
                />
              </Grid>
              <Grid item xs={6}>
                <ProgressStat
                  label="Errores"
                  value={progress.errores}
                  color={dark?"#FCA5A5":"#B91C1C"}
                  background={dark?"rgba(239,68,68,0.12)":"#FEF2F2"}
                />
              </Grid>
            </Grid>

            <Typography
              sx={{
                mt:2,
                fontSize:11.5,
                textAlign:"center",
                color:mutedColor,
                lineHeight:1.5
              }}
            >
              La descarga comenzará automáticamente cuando todas las facturas estén listas.
            </Typography>
          </Box>
        )}
      </Dialog>
    </>
  );
}
